/**
 * Heading and "Applied filters" line for the recommendation section (P1D-5538 FR-7, FR-8;
 * line design revised 2026-10-27). Pure: relative imports only, so the bare jest config can load it.
 */
import { KEPT_GROUP_KEYS, MOVE_IN_TOKEN, appliedGroups, groupRank, tokenKey } from "./filterTokens";
import { RecommendationSummary, RelaxationStep, removedFilterFields } from "./relaxation";

export const HEADING = "searchpage.recommendations.heading";
export const HEADING_BEYOND = "searchpage.recommendations.heading_beyond_filters";
export const SUBHEADING = "searchpage.recommendations.subheading";
export const MONTH_WIDENED = "searchpage.recommendations.month_widened";
export const APPLIED_FILTERS = "searchpage.recommendations.applied_filters";

export const SEPARATOR = " · ";
/** Applied filters named before the rest collapse into "+N". */
export const MAX_APPLIED_SHOWN = 2;

export type SubheadingType = "general" | "named";
export type RecommendationCopy = {
  headingKey: string;
  subheadingType: SubheadingType;
};

/** One group (today's heading) vs two or more ("Recommended properties beyond your filters"). */
export const selectRecommendationCopy = ({
  appliedGroupCount,
}: RecommendationSummary): RecommendationCopy => ({
  headingKey: appliedGroupCount <= 1 ? HEADING : HEADING_BEYOND,
  // The "Applied filters" line always names what changed.
  subheadingType: "named",
});

/** An applied item: the widened-month line (a copy key) or a filter group (its tab label). */
export type AppliedItem = { key: string } | { group: string };
export type AppliedFiltersLine = {
  applied: AppliedItem[];
  /** Applied items beyond `MAX_APPLIED_SHOWN`, shown as "+N". */
  moreCount: number;
  /** Removed groups in removal order, shown struck through. */
  removed: string[];
};

/**
 * "Applied filters: <applied> · +N · <removed>": the groups still applied in the section's final
 * step, the widened-month line first, then in the order the student applied them; at most two are
 * named; filters the section never removes (`KEPT_GROUP_KEYS`) count as applied. Removed
 * groups follow, all of them, in removal order. When the month was widened and
 * later removed too, only the widened line names it (PM decision B).
 */
export const buildAppliedFiltersLine = (
  { monthWidened, removedGroups }: Pick<RecommendationSummary, "monthWidened" | "removedGroups">,
  tokens: string[] = [],
  applicationOrder: string[] = [],
): AppliedFiltersLine => {
  const keptAlways = KEPT_GROUP_KEYS.filter((g) => tokens.some((t) => tokenKey(t) === g));
  const kept = [...appliedGroups(tokens), ...keptAlways]
    .filter((g) => !removedGroups.includes(g) && !(monthWidened && g === MOVE_IN_TOKEN))
    .sort(
      (a, b) => groupRank(a, tokens, applicationOrder) - groupRank(b, tokens, applicationOrder),
    );
  const items: AppliedItem[] = [
    ...(monthWidened ? [{ key: MONTH_WIDENED }] : []),
    ...kept.map((group) => ({ group })),
  ];
  return {
    applied: items.slice(0, MAX_APPLIED_SHOWN),
    moreCount: Math.max(0, items.length - MAX_APPLIED_SHOWN),
    removed: removedGroups.filter((g) => !(monthWidened && g === MOVE_IN_TOKEN)),
  };
};

/** `search_recommendations_section_viewed` params beyond the shared context (contract §7.2). */
export const sectionViewedParams = (
  plan: RelaxationStep[] = [],
  summary: RecommendationSummary | null = null,
  /** The applied filter tokens (`tokensFromFilters(filters)`). */
  appliedTokens: string[] = [],
) => {
  const removed = summary?.removedGroups || [];
  return {
    accepted_step_count: plan.length,
    pool_size: plan.reduce((sum, step) => sum + (step.pool || 0), 0),
    // removed_filters: option tokens; removed_filter_groups: group ids.
    ...removedFilterFields(appliedTokens, removed),
    removed_filter_count: removed.length,
    month_widened: Boolean(summary?.monthWidened),
    subheading_type: summary ? selectRecommendationCopy(summary).subheadingType : "general",
    step_count: summary?.stepCount ?? 0,
  };
};
